import React from 'react';
import { cn } from '@/lib/utils';
import { Info, CheckCircle, AlertTriangle } from 'lucide-react';

export type CalloutVariant = 'info' | 'success' | 'warning';

interface CalloutProps {
  title?: string;
  children: React.ReactNode;
  variant?: CalloutVariant;
  icon?: React.ReactNode;
  className?: string;
}

const variantStyles = {
  info: {
    container: 'bg-blue-50 dark:bg-blue-950/30 border-blue-100 dark:border-blue-900/50',
    title: 'text-blue-800 dark:text-blue-300',
    content: 'text-blue-700 dark:text-blue-400',
    icon: <Info className="h-5 w-5 text-blue-500 dark:text-blue-400" />
  },
  success: {
    container: 'bg-green-50 dark:bg-green-950/30 border-green-100 dark:border-green-900/50',
    title: 'text-green-800 dark:text-green-300',
    content: 'text-green-700 dark:text-green-400',
    icon: <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400" />
  },
  warning: {
    container: 'bg-amber-50 dark:bg-amber-950/30 border-amber-100 dark:border-amber-900/50',
    title: 'text-amber-800 dark:text-amber-300',
    content: 'text-amber-700 dark:text-amber-400',
    icon: <AlertTriangle className="h-5 w-5 text-amber-500 dark:text-amber-400" />
  }
};

export function Callout({ 
  title, 
  children, 
  variant = 'info', 
  icon, 
  className 
}: CalloutProps) {
  const styles = variantStyles[variant];
  
  return (
    <div className={cn(
      'rounded-lg border p-4 mb-4',
      styles.container,
      className
    )}>
      <div className="flex items-start">
        <div className="flex-shrink-0 mr-3 mt-0.5">
          {icon || styles.icon}
        </div>
        <div>
          {title && (
            <h4 className={cn('font-medium mb-1', styles.title)}>
              {title}
            </h4>
          )}
          <div className={cn('text-sm', styles.content)}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Callout;
